import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/features/dashboard/api/dashboard.api';
import { POLL_DEFAULT, POLL_SLOW } from '@/lib/polling';
import { useAuth } from '@/contexts/AuthContext';
import type { SystemResources } from '@/features/dashboard/types';

/**
 * One hook per widget so each fetches, polls and fails independently — a slow or
 * down endpoint degrades only its own widget, never the whole dashboard.
 */
const keys = {
  ready: ['dashboard', 'ready'] as const,
  stats: (w: number) => ['dashboard', 'stats', w] as const,
  users: ['dashboard', 'users'] as const,
  orgs: ['dashboard', 'orgs'] as const,
  system: ['dashboard', 'system'] as const,
  activity: ['dashboard', 'activity'] as const,
  notifications: ['dashboard', 'notifications'] as const,
};

export const useReady = () =>
  useQuery({ queryKey: keys.ready, queryFn: dashboardApi.ready, refetchInterval: POLL_DEFAULT, retry: false });

export const useStats = (windowHours = 24) =>
  useQuery({ queryKey: keys.stats(windowHours), queryFn: () => dashboardApi.statistics(windowHours), refetchInterval: POLL_SLOW });

// The mail-queue widget consumes `useQueueSummary` from `features/queue/hooks`
// (single owner of the /queue/summary query key — one fetch, one poll loop).

export const useUsersStats = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...keys.users, user?.tenantId],
    queryFn: () => dashboardApi.users(user!.tenantId),
    enabled: Boolean(user?.tenantId),
    staleTime: 300_000,
  });
};

export const useOrgStats = () =>
  useQuery({ queryKey: keys.orgs, queryFn: dashboardApi.organizations, staleTime: 300_000 });

/** Retained for the standalone resource components, which are not mounted until
 * a secured host-metrics endpoint is available. This deliberately performs no request. */
export const useSystemResources = () =>
  useQuery<SystemResources>({
    queryKey: keys.system,
    queryFn: async () => { throw new Error('Host metrics are not available.'); },
    enabled: false,
  });

export const useRecentActivity = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...keys.activity, user?.tenantId],
    queryFn: () => dashboardApi.activity(user!.tenantId, 8),
    enabled: Boolean(user?.tenantId),
    refetchInterval: POLL_SLOW,
  });
};

export const useNotifications = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...keys.notifications, user?.tenantId],
    queryFn: () => dashboardApi.notifications(user!.tenantId, 6),
    enabled: Boolean(user?.tenantId),
    refetchInterval: POLL_SLOW,
  });
};
